import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/styles/index'
import { Loader2 } from 'lucide-react'
import { useRef, useState } from 'react'

const buttonVariants = cva(
  'capitalize select-none inline-flex items-center justify-center gap-8 whitespace-nowrap text-20 font-medium transition-all disabled:cursor-not-allowed disabled:opacity-60 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover',
  {
    variants: {
      variant: {
        default: 'bg-[#0050C8] text-white hover:bg-[#0050C8]/90',
        blue: 'bg-[#0050C8] text-white hover:bg-[#0050C8]/90',
        purple: 'bg-[#764FF5] text-white hover:bg-[#764FF5]/90',
        red: 'bg-[#FF4343] text-white hover:bg-[#FF4343]/90',
        outline: 'border border-[#EBEBEB] bg-white text-[#6D7693] hover:bg-[#F5F7FA]',
        outlineBlue: 'border border-[#0050C8] bg-white text-[#0050C8] hover:bg-[#F5F7FA]',
        pageActive: 'text-[#0050C8] text-12 sm:text-16',
        pageDefault: 'text-[#9DABC9] text-12 sm:text-16',
      },
      size: {
        default: 'w-full h-46 px-16',
        '72': 'w-72 h-29 px-12 flex',
      },
      rounded: {
        default: 'rounded-50',
        none: 'rounded-0',
        '8': 'rounded-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounded: 'default',
    },
  },
)

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  // loading
  spin?: boolean
  // 防連點，毫秒
  debounce?: number
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ spin, className, variant, rounded, size, asChild = false, ...props }, ref) => {
  const [isLoading, setIsLoading] = useState(false)
  const debounceRef = useRef(0)
  const debounce = props?.debounce || 333

  /** 事件等待 */
  const onButtonClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (props?.disabled || spin || isLoading) return
    if (debounceRef.current > Date.now()) return
    debounceRef.current = Date.now() + debounce
    try {
      setIsLoading(true)
      await Promise.all([props.onClick?.(event)])
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <button className={cn(buttonVariants({ variant, size, rounded }), className)} ref={ref} {...props} onClick={onButtonClick}>
      {!!(isLoading || spin) && <Loader2 className="mr-6 h-16 w-16 animate-spin select-none" />}
      {props.children}
    </button>
  )
})
Button.displayName = 'Button'

export { Button, buttonVariants }
